//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { SeriesAreaOptions } from 'highcharts';

import {
  ITikTokEngagementMetrics,
  TikTokMetricSortKey,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';

export const tiktokEngagementStatBubbleTitles = {
  comments: 'Comments',
  engagementRate: 'Engagement Rate',
  likes: 'Likes',
  reach: 'Reach',
  shares: 'Shares',
  views: 'Views',
};

const IN_SELECTED_DATE_RANGE = ' in the selected date range.';

export const tiktokEngagementStatBubbleDefinitionsBase = {
  comments: 'Number of comments received',
  engagementRate:
    'Likes, comments, and shares your videos have received, divided by their reach',
  likes: 'Number of likes received',
  reach: 'Number of unique viewers',
  shares: 'Number of times your videos have been shared',
  views: 'Number of times your videos have been viewed',
};

export const tiktokEngagementStatBubbleTooltips = {
  comments: `${tiktokEngagementStatBubbleDefinitionsBase.comments}${IN_SELECTED_DATE_RANGE}`,
  engagementRate: `${tiktokEngagementStatBubbleDefinitionsBase.engagementRate}${IN_SELECTED_DATE_RANGE}`,
  likes: `${tiktokEngagementStatBubbleDefinitionsBase.likes}${IN_SELECTED_DATE_RANGE}`,
  reach: `${tiktokEngagementStatBubbleDefinitionsBase.reach}${IN_SELECTED_DATE_RANGE}`,
  shares: `${tiktokEngagementStatBubbleDefinitionsBase.shares}${IN_SELECTED_DATE_RANGE}`,
  views: `${tiktokEngagementStatBubbleDefinitionsBase.views}${IN_SELECTED_DATE_RANGE}`,
};

export enum TikTokEngagementPerformanceOption {
  Comments = 'Comments',
  EngagementRate = 'Engagement Rate',
  Likes = 'Likes',
  Reach = 'Reach',
  Shares = 'Shares',
  Views = 'Video Views',
}

export const tiktokEngagementMetricNames: Record<
  TikTokEngagementPerformanceOption,
  keyof ITikTokEngagementMetrics
> = {
  [TikTokEngagementPerformanceOption.Comments]: 'comments',
  [TikTokEngagementPerformanceOption.EngagementRate]: 'engagementRate',
  [TikTokEngagementPerformanceOption.Likes]: 'likes',
  [TikTokEngagementPerformanceOption.Reach]: 'reach',
  [TikTokEngagementPerformanceOption.Shares]: 'shares',
  [TikTokEngagementPerformanceOption.Views]: 'views',
} as const;

export const tiktokEngagementPerformanceDropdownOptions = [
  {
    label: TikTokEngagementPerformanceOption.Views,
    value: TikTokEngagementPerformanceOption.Views,
  },
  {
    label: TikTokEngagementPerformanceOption.Reach,
    value: TikTokEngagementPerformanceOption.Reach,
  },
  {
    label: TikTokEngagementPerformanceOption.Likes,
    value: TikTokEngagementPerformanceOption.Likes,
  },
  {
    label: TikTokEngagementPerformanceOption.Comments,
    value: TikTokEngagementPerformanceOption.Comments,
  },
  {
    label: TikTokEngagementPerformanceOption.Shares,
    value: TikTokEngagementPerformanceOption.Shares,
  },
  {
    label: TikTokEngagementPerformanceOption.EngagementRate,
    value: TikTokEngagementPerformanceOption.EngagementRate,
  },
];

export const mediaPerformanceTableHeaders = [
  TikTokMetricSortKey.PostedTimestamp,
  TikTokMetricSortKey.Views,
  TikTokMetricSortKey.Reach,
  TikTokMetricSortKey.WatchRate,
  TikTokMetricSortKey.Likes,
  TikTokMetricSortKey.Comments,
  TikTokMetricSortKey.Shares,
  TikTokMetricSortKey.EngagementRate,
];

export const mediaPerformanceTableHeaderTitles = {
  [TikTokMetricSortKey.Comments]: 'Comments',
  [TikTokMetricSortKey.EngagementRate]: 'Engagement Rate',
  [TikTokMetricSortKey.Likes]: 'Likes',
  [TikTokMetricSortKey.PostedTimestamp]: 'Posted Date',
  [TikTokMetricSortKey.Reach]: 'Reach',
  [TikTokMetricSortKey.Shares]: 'Shares',
  [TikTokMetricSortKey.Views]: 'Views',
  [TikTokMetricSortKey.WatchRate]: 'Watch Rate',
};

export const mediaPerformanceTableHeaderTooltipTitles = {
  [TikTokMetricSortKey.Comments]: 'Comments',
  [TikTokMetricSortKey.EngagementRate]: 'Engagement Rate',
  [TikTokMetricSortKey.Likes]: 'Likes',
  [TikTokMetricSortKey.PostedTimestamp]: 'Posted Date',
  [TikTokMetricSortKey.Reach]: 'Reach',
  [TikTokMetricSortKey.Shares]: 'Shares',
  [TikTokMetricSortKey.Views]: 'Views',
  [TikTokMetricSortKey.WatchRate]: 'Full Video Watched Rate',
};

export const mediaPerformanceTableHeaderTooltips = {
  [TikTokMetricSortKey.Comments]: 'Number of comments received.',
  [TikTokMetricSortKey.EngagementRate]:
    'Likes, comments, and shares the video has received, divided by its reach.',
  [TikTokMetricSortKey.Likes]: 'Number of likes received.',
  [TikTokMetricSortKey.PostedTimestamp]: '',
  // we use PostedTimestampTooltipContent component for this tooltip bc it contains a link
  [TikTokMetricSortKey.Reach]: 'Number of unique viewers.',
  [TikTokMetricSortKey.Shares]: 'Number of times the video has been shared.',
  [TikTokMetricSortKey.Views]: 'Number of times the video has been viewed.',
  [TikTokMetricSortKey.WatchRate]:
    'Percentage of users who viewed the whole video.',
};

export const tiktokEngagementTopMediaDropdownOptions = [
  {
    label: mediaPerformanceTableHeaderTitles.PostedTimestamp,
    value: TikTokMetricSortKey.PostedTimestamp,
  },
  {
    label: mediaPerformanceTableHeaderTitles.Views,
    value: TikTokMetricSortKey.Views,
  },
  {
    label: mediaPerformanceTableHeaderTitles.Reach,
    value: TikTokMetricSortKey.Reach,
  },
  {
    label: mediaPerformanceTableHeaderTitles.FullVideoWatchedRate,
    value: TikTokMetricSortKey.WatchRate,
  },
  {
    label: mediaPerformanceTableHeaderTitles.Likes,
    value: TikTokMetricSortKey.Likes,
  },
  {
    label: mediaPerformanceTableHeaderTitles.Comments,
    value: TikTokMetricSortKey.Comments,
  },
  {
    label: mediaPerformanceTableHeaderTitles.Shares,
    value: TikTokMetricSortKey.Shares,
  },
  {
    label: mediaPerformanceTableHeaderTitles.EngagementWithReach,
    value: TikTokMetricSortKey.EngagementRate,
  },
];

export const tiktokEngagementTooltips = {
  mediaPerformance: `Top performing TikToks are based on the date range and sort order selected`,
};

export const TOP_MEDIA_DEFAULT_LIMIT = 8;
export const TOP_MEDIA_LIMIT_OPTIONS = [8, 16, 32, 64];

export const TIKTOK_ENGAGEMENT_TABLE_MEDIA_SIZE = 90;
export const TOP_MEDIA_CARD_HEIGHT = 500;

export const BASE_CHART_OPTIONS: Highcharts.Options = {
  chart: {
    height: 350,
    width: 900,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
  },
  series: [
    {
      turboThreshold: 0,
      color: theme.colors.blue2,
    },
  ] as SeriesAreaOptions[],
};

export const ENGAGEMENT_EMPTY_DAILY_METRICS = {
  comments: 0,
  engagementRate: 0,
  likes: 0,
  reach: 0,
  shares: 0,
  views: 0,
};

export const CAPTION_BUTTON_HEIGHT = 34.5;
